import * as exif from '@ginpei/exif-orientation';
import fontkit from '@pdf-lib/fontkit';
import difference from 'lodash/difference';
import { PDFDocument, StandardFonts, degrees } from 'pdf-lib';
import { z } from 'zod';
import { FormBuilderFormFieldValidationSchema } from '../codecs/schema/formField';
// A library that takes a template document with a PDF file and an input
// and returns a pdf with the data inlines

/**
 * We use this type for hardcoded Document templates as it assists in ensuring correct
 * keys
 */
export type DocumentTemplate<K extends string> = {
  fileUrl?: string;
  filePath?: string;
  fields: {
    [field in K]: FormBuilderFormFieldValidationSchema;
  };
};

/**
 * We use this for Document templates that are saved in the database
 */
export const DocumentTemplateSchema = z.object({
  fileUrl: z.string().optional(),
  filePath: z.string().optional(),
  fields: z.record(z.string(), FormBuilderFormFieldValidationSchema)
});
export type DocumentTemplateSchema = z.infer<typeof DocumentTemplateSchema>;

// DataURI for signatures
type Values<K extends string> = { [k in K]: string };

function downloadPdf(pdfBytes: Uint8Array) {
  // Convert bytes to a Blob
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });

  // Create a URL for the Blob
  const url = URL.createObjectURL(blob);

  // Create a download link
  const a = document.createElement('a');
  a.href = url;
  a.download = 'download.pdf';

  // Append the link to the document, trigger the download, and remove the link
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  // Clean up by revoking the Blob URL
  URL.revokeObjectURL(url);
}

export async function streamToUint8Array(readableStream: ReadableStream) {
  const reader = readableStream.getReader();
  let chunks = [];

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    chunks.push(value);
  }

  let totalLength = chunks.reduce((acc, value) => acc + value.length, 0);
  let result = new Uint8Array(totalLength);

  let position = 0;
  for (let chunk of chunks) {
    result.set(chunk, position);
    position += chunk.length;
  }

  return result;
}

const fetchImageWithRetry = async (
  url: string,
  loggerFn: (message: string, error?: Error) => void
): Promise<Response> => {
  const MAX_RETRY = 3;
  const DELAY_MS = 1000;

  for (let attempt = 0; attempt < MAX_RETRY; attempt++) {
    try {
      // Fetch would only throw an error if there is a badly-formed request or network failure
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/fetch
      const response = await fetch(url);

      if (response.status >= 200 || response.status <= 300) {
        return response;
      }
    } catch (error) {
      if (error instanceof Error) {
        loggerFn('Failed to fetch image data', error);
      } else {
        loggerFn(
          'Failed to fetch image data',
          new Error(`Fetch image failed: ${error}`)
        );
      }
    }

    await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
  }

  loggerFn('Fetching image exceeds maximum retries');
  throw new Error('Fetching image exceeds maximum retries');
};

/**
 * Generate PDF file.
 *
 * This might look opaque, and also is. This is beucase it was initially just
 * made for the browser, but has been retrofitted to work isomorphically.
 *
 * Feel free to update the definition.
 *
 * @param doc
 * @param values
 * @param download
 * @param pathFilerReader
 * @param fontBytes
 * @returns
 */
export const generatePdf = async <K extends string>(
  doc: DocumentTemplate<K>,
  values: Values<K>,
  loggerFn: (message: string, error?: Error) => void,
  opts?: {
    download?: boolean;
    pathFilerReader?: (path: string) => Promise<Uint8Array>;
    customFont?: Uint8Array;
  }
) => {
  return generatePdfUntyped(
    doc,
    values,
    opts?.download,
    loggerFn,
    opts?.pathFilerReader,
    opts?.customFont
  );
};

export const generatePdfUntyped = async (
  doc: DocumentTemplateSchema,
  values: Record<string, string>,
  download = true,
  loggerFn: (message: string, error?: Error) => void,
  pathFilerReader?: (path: string) => Promise<Uint8Array>,
  customFont?: Uint8Array
) => {
  // Validates that fields in the document template and provided fields are the same
  const l1 = Object.keys(doc.fields).sort();
  const l2 = Object.keys(values).sort();
  if (difference(l1, l2).length > 0) {
    throw new Error(
      'Fields and values does not match, missing' +
        JSON.stringify(difference(l1, l2))
    );
  }

  let pdfBytes: Uint8Array | null = null;
  if (doc.fileUrl) {
    const stream = (await fetch(doc.fileUrl)).body;
    pdfBytes = stream ? await streamToUint8Array(stream) : null;
  } else if (doc.filePath && pathFilerReader) {
    pdfBytes = await pathFilerReader(doc.filePath);
  }

  if (!pdfBytes) {
    throw new Error(`Could not load PDF ${doc.filePath} ${doc.fileUrl}`);
  }

  const pdfDoc = await PDFDocument.load(pdfBytes);
  const pages = pdfDoc.getPages();

  // Using Immediate invocation so we can encapsulate the font config
  const helveticaFont = customFont
    ? await (async () => {
        pdfDoc.registerFontkit(fontkit);
        return pdfDoc.embedFont(customFont, { subset: true });
      })()
    : await pdfDoc.embedFont(StandardFonts.Helvetica);

  // Note that we use type coercions here which is proper use case as Object.keys is not properly typed
  for await (const fieldId of Object.keys(doc.fields)) {
    const field = doc.fields[fieldId];
    const placements = field.formFieldPlacements;

    for (const placement of placements ?? []) {
      const page = pages[placement.page];

      if (field.type === 'TEXT') {
        const lineHeight = placement.textFontSize
          ? placement.textFontSize * 1.1
          : 12 * 1.1;

        // We need to calculate text alignment manually since pdf-lib did not provide any high level function for this
        // https://github.com/Hopding/pdf-lib/issues/10#issuecomment-593108695
        const textToRender = values[fieldId];
        const textSize = placement.textFontSize ?? 12;
        const textWidth = helveticaFont.widthOfTextAtSize(
          textToRender,
          textSize
        );

        // Determine X position
        // This doesn't directly handle multiline
        // https://github.com/Hopding/pdf-lib/issues/701
        let x = placement.positionX;
        if (placement.textAlignment === 'RIGHT') {
          x += placement.width - textWidth;
        } else if (placement.textAlignment === 'CENTER') {
          x += (placement.width - textWidth) / 2;
        }

        page.drawText(textToRender, {
          x: x,
          y:
            page.getHeight() -
            placement.positionY -
            // pdf-lib renders the text with one line above the x/y origin but then continues below if the content exceeds the with.
            // To be in sync with the form builder rendering, we have to subtract that line
            lineHeight,
          maxWidth: placement.width,
          size: textSize,
          font: helveticaFont,
          lineHeight
        });
      } else if (field.type === 'CHARACTER_LIST') {
        for (let i = 0; i < (field.characterListLength ?? 0); i += 1) {
          const character = values[fieldId].split('').at(i) ?? '';
          const lineHeight = placement.textFontSize
            ? placement.textFontSize * 1.1
            : 12 * 1.1;

          page.drawText(character, {
            x:
              placement.positionX +
              i * (placement.width + (placement.characterListSpacing ?? 0)),
            y:
              page.getHeight() -
              placement.positionY -
              // pdf-lib renders the text with one line above the x/y origin but then continues below if the content exceeds the with.
              // To be in sync with the form builder rendering, we have to subtract that line
              lineHeight,
            size: placement.textFontSize ?? 12,
            font: helveticaFont,
            lineHeight
          });
        }
      } else if (
        (field.type === 'SIGNATURE' || field.type === 'IMAGE') &&
        (values[fieldId].startsWith('http') ||
          values[fieldId].startsWith('blob') ||
          // We support data urls
          values[fieldId].startsWith('data'))
      ) {
        const imageResponse = await fetchImageWithRetry(
          values[fieldId],
          loggerFn
        );

        const imageBlob = await imageResponse.blob();
        const imageArrayBuffer = await imageBlob.arrayBuffer();
        // We have to get the orientation from the file, because pdf-lib always reads images in landscape
        // There are several issue on GitHub regarding this. For example https://github.com/Hopding/pdf-lib/issues/1329
        const orientation = await exif.getOrientation(imageArrayBuffer);
        const image =
          imageBlob.type === 'image/png'
            ? await pdfDoc.embedPng(imageArrayBuffer)
            : await pdfDoc.embedJpg(imageArrayBuffer);
        const imageWidth = image.width;
        const imageHeight = image.height;
        const imageAspectRatio = imageWidth / imageHeight;

        // TODO: The placement can be centered after applying the aspect ratio
        page.drawImage(image, {
          x: placement.positionX,
          y:
            page.getHeight() -
            placement.positionY -
            // pdf-lib renders the image above the x/y origin
            // To be in sync with the form builder rendering, we have to subtrcat the height
            placement.height +
            // If the image is rotated we have to add the height (it's the height in landscape as it's read from pdf-lib)
            (orientation?.rotation ? placement.height : 0),
          width:
            // pdf-lib stretches the image; in order to achieve "object-fit: contain", we have to apply the aspect ratio
            placement.width > placement.height
              ? placement.height * imageAspectRatio
              : placement.width,
          height:
            // pdf-lib stretches the image; in order to achieve "object-fit: contain", we have to apply the aspect ratio
            placement.width > placement.height
              ? placement.height
              : placement.width / imageAspectRatio,
          rotate: orientation?.rotation
            ? degrees(-1 * orientation.rotation)
            : undefined
        });
      } else if (field.type === 'CHECKBOX') {
        const value = values[fieldId].toLocaleLowerCase();
        if (value === 'true') {
          // Load the image into the PDF file
          const imageResponse = await fetch(checkMarkPng);
          const imageBlob = await imageResponse.blob();
          const image = await pdfDoc.embedPng(await imageBlob.arrayBuffer());

          page.drawImage(image, {
            x: placement.positionX,
            y:
              page.getHeight() -
              placement.positionY -
              // pdf-lib renders the image above the x/y origin
              // To be in sync with the form builder rendering, we have to subtrcat the height
              placement.height,
            width: placement.width,
            height: placement.height
          });
        }
      }
    }
  }

  // Serialize the PDFDocument to bytes (a Uint8Array)
  const pdfBytesResult = await pdfDoc.save();

  if (download) downloadPdf(pdfBytesResult);

  return pdfBytesResult;
};

const checkMarkPng =
  'data:image/png;base64,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';
